import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Icon } from "@/components/Icon";
import { Input } from "@/components/Input";
import { PasswordInput } from "@/components/PasswordInput";
import { Switch } from "@/components/Switch";
import { startAuthentication } from "@simplewebauthn/browser";
import { authApi } from "@/lib/api";
import { Tooltip } from "@/components/Tooltip";
import { Logo } from "@/components/Logo";

export const Login: React.FC = () => {
    const { t } = useTranslation("global");
    const { login, loginWithWebauthn, complete2fa } = useAuth();
    const { showSuccess, showError } = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        if (searchParams.get("verified") === "1") {
            showSuccess(t("auth.email_verified_success"));
            searchParams.delete("verified");
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams, showSuccess, t]);

    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [isForgotPassword, setIsForgotPassword] = useState<boolean>(false);

    // 2FA states
    const [is2faPending, setIs2faPending] = useState<boolean>(false);
    const [tempToken, setTempToken] = useState<string>("");
    const [twoFactorCode, setTwoFactorCode] = useState<string>("");
    const [trustDevice, setTrustDevice] = useState<boolean>(false);
    const destination = location.state?.from || "/";

    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        if (is2faPending) {
            if (!twoFactorCode.trim()) {
                showError(t("auth.two_factor_empty"));
                return;
            }
            setLoading(true);
            try {
                await complete2fa(twoFactorCode.trim(), trustDevice, tempToken);
                navigate(destination, { replace: true });
            } catch (err: any) {
                const msg =
                    err instanceof Error
                        ? err.message
                        : t("auth.generic_error");
                showError(msg);
            } finally {
                setLoading(false);
            }
            return;
        }

        if (!email.trim() || !password.trim()) {
            showError(t("auth.empty_credentials"));
            return;
        }

        setLoading(true);
        try {
            const res = await login(email.trim(), password);
            if (res?.requires_2fa) {
                setTempToken(res.temp_token);
                setIs2faPending(true);
            } else {
                navigate(destination, { replace: true });
            }
        } catch (err: any) {
            const msg =
                err instanceof Error ? err.message : t("auth.generic_error");
            showError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim()) {
            showError(t("auth.empty_credentials"));
            return;
        }
        setLoading(true);
        try {
            await authApi.forgotPassword(email.trim());
            showSuccess(
                t(
                    "auth.forgot_password_success",
                    "Si el email existe, se han enviado las instrucciones."
                )
            );
            setIsForgotPassword(false);
        } catch (err: any) {
            showError(err.message || t("auth.generic_error"));
        } finally {
            setLoading(false);
        }
    };

    const handleBiometricLogin = async () => {
        setLoading(true);
        try {
            const options = await authApi.webauthnLoginOptions({
                email: email.trim() || undefined,
            });
            const asseResp = await startAuthentication({
                optionsJSON: options,
            });
            const res = await loginWithWebauthn(asseResp);

            if (res?.requires_2fa) {
                setTempToken(res.temp_token);
                setIs2faPending(true);
            } else {
                navigate("/");
            }
        } catch (err: any) {
            console.error(err);
            if (
                err.name === "NotAllowedError" ||
                (err.message && err.message.includes("not allowed"))
            ) {
                showError(
                    t(
                        "settings.security.biometrics.cancelled",
                        "Operación cancelada"
                    )
                );
            } else {
                showError(err.message || t("auth.generic_error"));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-dvh bg-page flex flex-col justify-center items-center p-4 sm:p-6">
            <div className="max-w-md w-full bg-box rounded-2xl shadow-xl border border-slate-200/80 overflow-hidden">
                {/* Header */}
                <div className="px-8 pt-8 pb-6 text-center flex flex-col items-center">
                    <Logo className="mx-auto w-44 md:w-52 h-auto object-contain" />
                    {/*
                    <p className="text-xs text-slate-400 mt-2 font-medium tracking-wide">
                        {t('auth.login_subtitle')}
                    </p>
                    */}
                </div>

                {/* Form */}
                <form
                    onSubmit={
                        isForgotPassword ? handleForgotPassword : handleSubmit
                    }
                    className="p-7 space-y-4"
                >
                    {isForgotPassword ? (
                        <>
                            <div className="text-center mb-4">
                                <Icon
                                    value="mail"
                                    size={40}
                                    className="mx-auto text-primary mb-2"
                                />
                                <h3 className="font-bold text-slate-800 dark:text-zinc-100">
                                    {t("auth.forgot_password_title")}
                                </h3>
                                <p className="text-sm text-slate-500">
                                    {t("auth.forgot_password_desc")}
                                </p>
                            </div>
                            <Input
                                type="email"
                                required
                                requiredLabel={false}
                                label={t("auth.email_label")}
                                icon={<Icon value="mail" size={16} />}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3 bg-primary hover:bg-primary-900 text-white font-bold text-sm rounded-xl shadow-md transition-all disabled:opacity-50 mt-2 cursor-pointer"
                            >
                                {loading
                                    ? t("auth.logging_in")
                                    : t("auth.send_instructions")}
                            </button>
                            <div className="flex justify-center">
                                <button
                                    type="button"
                                    className="text-primary hover:opacity-90 flex justify-center text-[14px]"
                                    onClick={() => setIsForgotPassword(false)}
                                >
                                    {t("auth.back_to_login")}
                                </button>
                            </div>
                        </>
                    ) : is2faPending ? (
                        <>
                            <div className="text-center mb-4">
                                <Icon
                                    value="shieldCheck"
                                    size={48}
                                    className="mx-auto text-primary mb-2"
                                />
                                <h3 className="text-lg font-bold text-slate-800 dark:text-zinc-100">
                                    {t("auth.two_factor_title")}
                                </h3>
                                <p className="text-sm text-slate-500">
                                    {t("auth.two_factor_desc")}
                                </p>
                            </div>
                            <div className="max-w-xs mx-auto">
                                <Input
                                    type="text"
                                    label={t("auth.two_factor_code")}
                                    labelClassName="text-center"
                                    icon={<Icon value="lock" size={16} />}
                                    value={twoFactorCode}
                                    onChange={(e) =>
                                        setTwoFactorCode(e.target.value)
                                    }
                                    placeholder={t(
                                        "auth.two_factor_placeholder"
                                    )}
                                    autoComplete="one-time-code"
                                    inputMode="numeric"
                                />
                            </div>
                            <div className="mt-4 flex justify-center">
                                <Switch
                                    id="trustDevice"
                                    checked={trustDevice}
                                    onChange={setTrustDevice}
                                    label={t("auth.two_factor_trust")}
                                    position="left"
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <Input
                                type="text"
                                required
                                requiredLabel={false}
                                label={t("auth.email_label")}
                                icon={<Icon value="mail" size={16} />}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="username"
                            />

                            <div>
                                <PasswordInput
                                    label={t("auth.password_label")}
                                    required
                                    requiredLabel={false}
                                    icon={<Icon value="lock" size={16} />}
                                    value={password}
                                    onChange={(e) =>
                                        setPassword(e.target.value)
                                    }
                                />
                                <div className="text-right">
                                    <button
                                        type="button"
                                        className="text-[12px] text-primary hover:opacity-90 cursor-pointer"
                                        onClick={() =>
                                            setIsForgotPassword(true)
                                        }
                                    >
                                        {t("auth.forgot_password")}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    {!isForgotPassword && (
                        <>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3 bg-primary hover:bg-primary-900 text-white font-bold text-sm rounded-xl shadow-md transition-all disabled:opacity-50 mt-2 cursor-pointer"
                            >
                                {loading
                                    ? t("auth.logging_in")
                                    : t("auth.login_button")}
                            </button>

                            <div className="flex flex-col items-center justify-center mt-2">
                                <Tooltip
                                    title={t(
                                        "settings.security.biometrics.title"
                                    )}
                                >
                                    <button
                                        type="button"
                                        onClick={handleBiometricLogin}
                                        disabled={loading}
                                        className="transition-colors disabled:opacity-50 flex items-center justify-center"
                                        title={t(
                                            "settings.security.biometrics.title"
                                        )}
                                    >
                                        <Icon
                                            value="fingerprint"
                                            size={48}
                                            className="text-primary btn-effect"
                                        />
                                    </button>
                                </Tooltip>
                            </div>

                            {is2faPending && (
                                <button
                                    type="button"
                                    className="text-sm text-primary hover:text-primary-900 block w-full text-center"
                                    onClick={() => setIs2faPending(false)}
                                >
                                    {t("auth.two_factor_back")}
                                </button>
                            )}
                        </>
                    )}
                </form>
            </div>
        </div>
    );
};
